import { useMutation } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { Icon } from '@column/column-ui-kit';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  InputGroupAddon,
  PhoneInput,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  isValidPhoneNumber,
} from '@column/ui';

import { RecaptchaAction, useReCAPTCHA } from '~/lib/recaptcha';
import { FactorType, FactorTypeResponse, UserRepository } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { log } from '~/util';

import { AuthenticateModal } from './_components/AuthenticateModal';
import { FieldStatus, StatusOrAction, TRAILING_STATUS_ADDON_CLASS_NAME } from './_components/FieldStatus';

const toE164 = (value: string) => value.replace(/[^\d+]/g, '');

const ICON_CLASS_NAME = '[--icon-size-x:14px] [--icon-size-y:14px]';
const ERROR_ID = 'mfa-phone-number-error';

export const validateMfaPhoneNumber = (value: unknown, savedNumber?: string) =>
  !value ||
  value === toE164(savedNumber ?? '') ||
  isValidPhoneNumber(String(value)) ||
  'Please enter a valid phone number for the selected country';

interface Props {
  error?: string;
  onPhoneNumberChange: (phoneNumber: string) => void;
  onBlur?: () => void;
}

export const SectionMfaSms: React.FC<Props> = ({ onPhoneNumberChange, onBlur, error: phoneError }: Props) => {
  const { setError, trigger } = useFormContext<{ phoneNumber?: string }>();
  const [isAuthenticateModalOpen, setIsAuthenticateModalOpen] = useState(false);
  const { currentUser, refetchCurrentUser } = useSessionStore();
  const { addDangerNotification, addInfoNotification, addSuccessNotification } = useNotificationStore();
  const [isDisableDialogOpen, setIsDisableDialogOpen] = useState(false);

  const closeModal = useCallback(() => {
    setIsAuthenticateModalOpen(false);
  }, []);

  const executeReCAPTCHA = useReCAPTCHA();

  const [phoneValue, setPhoneValue] = useState<string>('');
  const phoneNumber = toE164(phoneValue);

  useEffect(() => {
    if (currentUser) {
      setPhoneValue(currentUser.phoneNumber ?? '');
    }
  }, [currentUser]);

  const setupSmsMfaMutation = useMutation({
    mutationFn: ({ reCaptchaToken, factorName }: { reCaptchaToken?: string; factorName: string }) =>
      UserRepository.setupMfa(reCaptchaToken, {
        factorName,
        factorType: FactorType.SMS,
      }),
    onSuccess: () => {
      log({
        name: 'User enable MFA requested',
        context: currentUser,
      });

      addInfoNotification({
        content: 'Please confirm your phone number to finish 2FA setup.',
      });

      refetchCurrentUser();
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Something went wrong, please try again.';
      addDangerNotification({
        content: message,
      });
    },
  });

  const handleEnableSmsMfa = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (!currentUser) {
        return;
      }

      if (!phoneNumber || !isValidPhoneNumber(phoneNumber)) {
        setError('phoneNumber', {
          type: 'validate',
          message: phoneNumber
            ? 'Please enter a valid phone number for the selected country'
            : 'Enter a phone number to enable two-factor authentication',
        });
        return;
      }

      if (setupSmsMfaMutation.isPending) {
        return;
      }

      executeReCAPTCHA(RecaptchaAction.UpdateUser, { optional: true })
        .then((reCaptchaToken) =>
          currentUser
            .update({ phoneNumber }, reCaptchaToken)
            .then(() =>
              executeReCAPTCHA(RecaptchaAction.MfaSetup, { optional: true })
                .then((reCaptchaTokenMfa) => {
                  setupSmsMfaMutation.mutate({ reCaptchaToken: reCaptchaTokenMfa, factorName: phoneNumber });
                })
                .catch((err: Error) => {
                  addDangerNotification({
                    content: err.message,
                  });
                })
            )
            .catch((error: Error) => {
              addDangerNotification({
                content: error.message,
              });
            })
        )
        .catch((err: Error) => {
          addDangerNotification({
            content: err.message,
          });
        });
    },
    [
      currentUser,
      phoneNumber,
      setupSmsMfaMutation,
      addDangerNotification,
      executeReCAPTCHA,
      addInfoNotification,
      refetchCurrentUser,
      setError,
    ]
  );

  const smsFactor = currentUser?.factors.find((factor) => factor.factorType === FactorTypeResponse.SMS);

  const disableSmsMfaMutation = useMutation({
    mutationFn: ({ factorId }: { factorId: string }) => UserRepository.deleteFactor({ factorId }),
    onSuccess: () => {
      addSuccessNotification({ content: 'SMS two-factor authentication disabled' });
      void refetchCurrentUser();
    },
    onError: (error: unknown) => {
      addDangerNotification({
        content: error instanceof Error ? error.message : 'Failed to disable SMS two-factor authentication',
      });
    },
  });

  const handleConfirmMfa = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsAuthenticateModalOpen(true);
    },
    [setIsAuthenticateModalOpen]
  );

  const isMfaPending = useMemo(
    () => currentUser?.factors.some((factor) => factor.factorType === FactorTypeResponse.SMS && !factor.factorVerified),
    [currentUser]
  );

  const isMfaVerified = useMemo(
    () => currentUser?.factors.some((factor) => factor.factorType === FactorTypeResponse.SMS && factor.factorVerified),
    [currentUser]
  );

  if (!currentUser) {
    return null;
  }

  const enableButton = (
    <Button
      type="button"
      variant="muted"
      size="xs"
      onClick={handleEnableSmsMfa}
      disabled={!currentUser.isEmailVerified || setupSmsMfaMutation.isPending}
    >
      {setupSmsMfaMutation.isPending ? <Spinner className="size-3" /> : <Icon.PhoneAdd className={ICON_CLASS_NAME} />}
      Enable
    </Button>
  );

  const action = isMfaPending ? (
    <Button type="button" variant="muted" size="xs" onClick={handleConfirmMfa}>
      <Icon.Checkmark className={ICON_CLASS_NAME} />
      Confirm
    </Button>
  ) : isMfaVerified ? (
    <Button
      type="button"
      variant="muted"
      size="xs"
      disabled={disableSmsMfaMutation.isPending}
      onClick={() => setIsDisableDialogOpen(true)}
    >
      {disableSmsMfaMutation.isPending && <Spinner className="size-3" />}
      Disable
    </Button>
  ) : currentUser.isEmailVerified ? (
    enableButton
  ) : (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          className="flex rounded-lg focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
        >
          {enableButton}
        </span>
      </TooltipTrigger>
      <TooltipContent>Verify email first</TooltipContent>
    </Tooltip>
  );

  return (
    <>
      <PhoneInput
        value={phoneValue}
        onChange={(value) => {
          setPhoneValue(value);
          onPhoneNumberChange(toE164(value));
          if (phoneError) {
            void trigger('phoneNumber');
          }
        }}
        onBlur={onBlur}
        aria-label="Phone number"
        aria-invalid={!!phoneError || undefined}
        aria-describedby={phoneError ? ERROR_ID : undefined}
      >
        <InputGroupAddon align="inline-end" className={TRAILING_STATUS_ADDON_CLASS_NAME}>
          <StatusOrAction
            status={
              isMfaVerified ? (
                <FieldStatus size="xs" tone="success" label="Enabled" />
              ) : isMfaPending ? (
                <FieldStatus size="xs" tone="pending" label="Pending" />
              ) : (
                <FieldStatus size="xs" tone="warning" label="Not enabled" />
              )
            }
            action={action}
          />
        </InputGroupAddon>
      </PhoneInput>
      {phoneError && (
        <p id={ERROR_ID} className="m-0 mt-1.5 text-xs leading-4 text-destructive">
          {phoneError}
        </p>
      )}
      <AuthenticateModal isOpen={isAuthenticateModalOpen} onClose={closeModal} />
      <AlertDialog open={isDisableDialogOpen} onOpenChange={setIsDisableDialogOpen}>
        <AlertDialogContent className="gap-6 data-[size=default]:sm:max-w-sm">
          <AlertDialogHeader className="gap-2">
            <AlertDialogTitle className="text-base leading-5">Disable SMS Two-Factor Authentication</AlertDialogTitle>
            <AlertDialogDescription className="leading-5">
              Two-factor authentication codes will no longer be sent to this phone number. Disabling will leave your
              account less secure.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="sm:justify-between">
            <AlertDialogCancel variant="secondary" size="sm">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              variant="danger"
              size="sm"
              onClick={() => smsFactor && disableSmsMfaMutation.mutate({ factorId: smsFactor.factorId })}
            >
              Disable
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
